import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
  conversationMatchesCurrentView,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import * as Sentry from '@sentry/vue';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import router from '../../../routes';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

// One list request at a time: a newer fetch, a reconnect refresh or a cached tab switch cancels the
// request still in flight, so a late response can never overwrite the list the agent now looks at.
const conversationListRequest = useAbortableRequest();

// In-flight message history requests, keyed by conversation and cursor. Switching A → B → A before
// A has loaded reuses A's request instead of starting a second one for the same messages.
const pendingHistoryRequests = new Map();

const shareHistoryRequest = (key, startRequest) => {
  if (pendingHistoryRequests.has(key)) return pendingHistoryRequests.get(key);

  const request = startRequest().finally(() =>
    pendingHistoryRequests.delete(key)
  );
  pendingHistoryRequests.set(key, request);
  return request;
};

// actions
const actions = {
  getConversation: async ({ state, commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      const conversationData = response.data;
      const currentConversationIndex = state.allConversations.findIndex(
        c => c.id === conversationId
      );
      if (currentConversationIndex > -1) {
        commit(types.UPDATE_CONVERSATION, conversationData);
      } else {
        commit(types.ADD_CONVERSATION, conversationData);
      }
      commit(
        `contacts/${types.SET_CONTACT_ITEM}`,
        conversationData.meta.sender
      );
    } catch (error) {
      if (error.status === 410 && error.response?.data?.merged) {
        const data = error.response.data;
        const path = frontendURL(
          conversationUrl({
            accountId: data.account_id,
            id: data.target_conversation_id,
          })
        );
        await router.push({ path });
      }
    }
  },

  // Refreshes one conversation after a reconnect. The list merge keeps the loaded messages and,
  // unlike getConversation, does not scroll the thread or refetch label suggestions.
  refreshConversation: async ({ commit, dispatch }, conversationId) => {
    try {
      const { data } = await ConversationApi.show(conversationId);
      commit(types.SET_ALL_CONVERSATION, [data]);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data.meta.sender);
      dispatch('conversationLabels/setConversationLabel', {
        id: data.id,
        data: data.labels,
      });
    } catch (error) {
      // Ignore error, the reconnect flow continues with the message sync
    }
  },

  fetchAllConversations: async (
    { commit, state, dispatch },
    { replaceExisting = false } = {}
  ) => {
    return conversationListRequest.run(async signal => {
      commit(types.SET_LIST_LOADING_STATUS);
      try {
        const params = state.conversationFilters;
        const {
          data: { data },
        } = await ConversationApi.get(params, { signal });

        if (signal.aborted) return;

        buildConversationList(
          { commit, dispatch },
          params,
          data,
          params.assigneeType,
          { replaceExisting }
        );
      } catch (error) {
        if (signal.aborted) return;

        commit(types.CLEAR_LIST_LOADING_STATUS);
        Sentry.captureException(error);
      }
    });
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    return conversationListRequest.run(async signal => {
      const { replaceExisting = false, ...requestParams } = params;
      commit(types.SET_LIST_LOADING_STATUS);
      try {
        const { data } = await ConversationApi.filter(requestParams, {
          signal,
        });

        if (signal.aborted) return;

        const filterType = requestParams.filterType || 'appliedFilters';
        buildConversationList(
          { commit, dispatch },
          requestParams,
          data,
          filterType,
          { replaceExisting }
        );
      } catch (error) {
        if (signal.aborted) return;

        commit(types.CLEAR_LIST_LOADING_STATUS);
        Sentry.captureException(error);
      }
    });
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  requestContactInfo: async ({ commit }, conversationId) => {
    const { data } = await ConversationApi.requestContactInfo(conversationId);
    commit(types.ADD_MESSAGE, data);
    return data;
  },

  fetchPreviousMessages: ({ commit }, data) => {
    const requestKey = JSON.stringify([
      'history',
      data.conversationId,
      data.before,
      data.after,
    ]);
    return shareHistoryRequest(requestKey, async () => {
      try {
        const {
          data: { meta, payload },
        } = await MessageApi.getPreviousMessages(data);
        commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
          id: data.conversationId,
          data: meta,
        });
        commit(types.SET_PREVIOUS_CONVERSATIONS, {
          id: data.conversationId,
          data: payload,
        });
        if (!payload.length) {
          // The fetched conversation, not the selected one: the agent may have switched meanwhile.
          commit(types.SET_ALL_MESSAGES_LOADED, data.conversationId);
        }
      } catch (error) {
        Sentry.captureException(error);
      }
    });
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = [];
    if (!conversationId) return;

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data?.payload ?? [];
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const findChat = () =>
      state.allConversations.find(
        conversation => conversation.id === conversationId
      );
    if (!findChat()) return Promise.resolve();

    return shareHistoryRequest(
      JSON.stringify(['sync', conversationId]),
      async () => {
        try {
          // Fetch all the messages after the last message id
          const {
            data: { meta, payload },
          } = await MessageApi.getPreviousMessages({
            conversationId,
          });
          commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
            id: conversationId,
            data: meta,
          });
          // Look the chat up again: a reconnect or list refresh can replace the conversation
          // object while the request runs, and messages added to the old one would be lost.
          const chat = findChat();
          if (!chat) return;
          const loadedIds = new Set(chat.messages.map(message => message.id));
          const missingMessages = payload.filter(
            message => !loadedIds.has(message.id)
          );
          // created_at has whole seconds; the id keeps messages of the same second in the order they
          // were created (e.g. two activities of one conversation move), whichever the chat held first.
          const sortedMessages = [...chat.messages, ...missingMessages].sort(
            (a, b) =>
              new Date(a.created_at) - new Date(b.created_at) || a.id - b.id
          );
          commit(types.SET_MISSING_MESSAGES, {
            id: conversationId,
            data: sortedMessages,
          });
          commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
            conversationId,
            messageId: null,
          });
          dispatch('markMessagesRead', { id: conversationId }, { root: true });
        } catch (error) {
          // Handle error
        }
      }
    );
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch, state }, { data }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED);

    const conversationId = data.id;
    const chat =
      state.allConversations.find(c => c.id === conversationId) || data;
    if (chat.dataFetched !== undefined) return;

    try {
      await dispatch('syncActiveConversationMessages', { conversationId });
    } catch (error) {
      // Ignore error
    } finally {
      // Write through a mutation that looks the chat up by id. The conversation
      // object is replaced wholesale by UPDATE_CONVERSATION / SET_ALL_CONVERSATION,
      // so setting the flag on the object captured above can land on a detached
      // copy, leaving the live one stuck on `dataFetched === undefined` - which
      // keeps the message spinner on and disables loading older messages.
      commit(types.SET_CONVERSATION_DATA_FETCHED, conversationId);
    }
  },

  assignAgent: async ({ dispatch }, { conversationId, agentId }) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
      });
      dispatch('setCurrentChatAssignee', response.data);
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatAssignee({ commit }, assignee) {
    commit(types.ASSIGN_AGENT, assignee);
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null, skipCsat = false }
  ) => {
    const {
      data: {
        payload: {
          current_status: updatedStatus,
          snoozed_until: updatedSnoozedUntil,
        } = {},
      } = {},
    } = await ConversationApi.toggleStatus({
      conversationId,
      status,
      snoozedUntil,
      skipCsat,
    });
    commit(types.CHANGE_CONVERSATION_STATUS, {
      conversationId,
      status: updatedStatus,
      snoozedUntil: updatedSnoozedUntil,
    });
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    return dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    const response = hasMessageFailedWithExternalError(pendingMessage)
      ? await MessageApi.retry(conversationId, id)
      : await MessageApi.create(pendingMessage);
    commit(types.ADD_MESSAGE, {
      ...response.data,
      status: MESSAGE_STATUS.SENT,
    });
    commit(types.ADD_CONVERSATION_ATTACHMENTS, {
      ...response.data,
      status: MESSAGE_STATUS.SENT,
    });
  },

  addMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
  },

  updateMessage({ commit }, message) {
    commit(types.ADD_MESSAGE, message);
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    const { data } = await MessageApi.delete(conversationId, messageId);
    commit(types.ADD_MESSAGE, data);
    commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
  },

  async deleteMessageRecording({ commit }, { conversationId, messageId }) {
    const { data } = await MessageApi.deleteRecording(
      conversationId,
      messageId
    );
    commit(types.ADD_MESSAGE, data);
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const {
      meta: { sender },
    } = conversation;
    if (conversationMatchesCurrentView(conversation, state, rootState)) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, state, dispatch, rootState }, conversation) {
    const {
      meta: { sender },
    } = conversation;

    const currentConversationIndex = state.allConversations.findIndex(
      c => c.id === conversation.id
    );
    if (currentConversationIndex > -1) {
      commit(types.UPDATE_CONVERSATION, conversation);
    } else if (conversationMatchesCurrentView(conversation, state, rootState)) {
      commit(types.ADD_CONVERSATION, conversation);
    }

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  // Errors are rethrown: the API refuses to block a public (freemail) domain and
  // the caller has to surface that instead of reporting success.
  muteConversationContactDomain: async ({ commit }, conversationId) => {
    await ConversationApi.blockDomain(conversationId);
    commit(types.MUTE_CONVERSATION_CONTACT_DOMAIN);
  },

  unmuteConversationContactDomain: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unblockDomain(conversationId);
      commit(types.UNMUTE_CONVERSATION_CONTACT_DOMAIN);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    await ConversationApi.sendEmailTranscript({ conversationId, email });
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    const response = await ConversationApi.updateCustomAttributes({
      conversationId,
      customAttributes,
    });
    const { custom_attributes } = response.data;
    commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, custom_attributes);
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  setConversationFiltersSortBy({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS_SORT_BY, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  invalidateConversationListRequests({ commit }) {
    conversationListRequest.abort();
    commit(types.CLEAR_LIST_LOADING_STATUS);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  getInboxCaptainAssistantById: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.getInboxAssistant(conversationId);
      commit(types.SET_INBOX_CAPTAIN_ASSISTANT, response.data);
    } catch (error) {
      // Handle error
    }
  },

  translateConversation: async (
    { commit },
    { conversationId, targetLanguage = 'cs' }
  ) => {
    try {
      commit(types.SET_CONVERSATION_TRANSLATE_LOADING, {
        conversationId,
        loading: true,
      });
      await ConversationApi.translate(conversationId, targetLanguage);
    } catch (error) {
      commit(types.SET_CONVERSATION_TRANSLATE_LOADING, {
        conversationId,
        loading: false,
      });
      throw error;
    }
  },

  mergeConversation: async (
    { commit },
    { conversationId, targetConversationId }
  ) => {
    await ConversationApi.merge(conversationId, targetConversationId);
    commit(types.DELETE_CONVERSATION, conversationId);
  },

  batchMergeConversations: async (
    { commit },
    { targetConversationDisplayId, sourceConversationDisplayIds }
  ) => {
    await ConversationApi.batchMerge(
      targetConversationDisplayId,
      sourceConversationDisplayIds
    );
    sourceConversationDisplayIds.forEach(id => {
      commit(types.DELETE_CONVERSATION, id);
    });
  },

  moveConversation: async ({ commit }, { conversationId, targetInboxId }) => {
    await ConversationApi.move(conversationId, targetInboxId);
    commit(types.DELETE_CONVERSATION, conversationId);
  },

  deleteConversation: ({ commit }, conversationId) => {
    commit(types.DELETE_CONVERSATION, conversationId);
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
